import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router';
import { connect } from 'react-redux';
import { Row, Column, Drawer } from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { getCurrencies } from 'store/entities/user-settings/user-settings';
import { ImportFromPriceBookModal } from 'src/views/cost-setup/cost-import-from-pricebook/cost-import-from-price-book.modal';
import {
  selectCostCodesCategoryOptions,
  selectCostCodesCodeAndNameToIdMap,
  selectCostCodesIdToCodeAndNameMap,
  selectCostCodesIdToCodeMap,
} from 'src/store/entities/cost-codes/selectors';
import { getCostCodesItems } from 'src/store/entities/cost-codes/cost-codes';
import {
  addCostItem,
  costItemSelected,
  updateCostItem,
  showDeleteCostItemModalUpdated,
  removeCostItem,
  addCostCategory,
  showDeleteCostCategoryModalUpdated,
  removeCostCategory,
  costCategorySelected,
  updateCostCategory,
  reorderElements,
  addCostItemsFromPriceBook,
} from 'store/entities/templates/templates';
import {
  getSelectedId,
  selectSectionTypes,
  selectTemplatesModalVisibility,
} from 'src/store/entities/templates/selectors';
import { clearPriceBook, getPriceBookCompanyItems } from 'src/store/entities/price-book/price-book';
import { CostElementsList } from './cost-elements.list';
import { CostItemDeleteModal } from './cost-item-delete.modal';
import { CostItemForm } from './cost-item.form';
import { CostCategoryForm } from './cost-category.form';
import { CostCategoryDeleteModal } from './cost-category-delete.modal';

import styles from '../templates.module.less';

const CostSetup = ({
  entity,
  templates,
  elements,
  addCostItem,
  showDeleteCostItemModal,
  showDeleteCostItemModalUpdated,
  selectedId,
  removeCostItem,
  activeCostItem,
  getCurrencies,
  addCostCategory,
  activeCostCategory,
  showDeleteCostCategoryModalUpdated,
  showDeleteCostCategoryModal,
  removeCostCategory,
  costItemSelected,
  costCategorySelected,
  reorderElements,
  updateCostCategory,
  updateCostItem,
  groupedOperations,
  getCostCodesItems,
  costCodesCategories,
  costCodesIdToCodeAndNameMap,
  costCodesCodeAndNameToIdMap,
  costCodesIdToCodeMap,
  depthUnit,
  volumeUnit,
  addCostItemsFromPriceBook,
  getPriceBookCompanyItems,
  clearPriceBook,
}) => {
  const { t } = useTranslation();
  const { company: companyId } = useParams();

  const [activeForm, setActiveForm] = useState(null);
  const [showImportFromPriceBook, setShowImportFromPriceBook] = useState(false);
  const [importWithLink, setImportWithLink] = useState(false);
  const [selectedImportId, setSelectedImportId] = useState(null);

  useEffect(() => {
    getCurrencies();
    getCostCodesItems();
    getPriceBookCompanyItems();
    return () => {
      clearPriceBook();
    };
  }, []);

  const onClickAddItem = (parentId = null) => {
    addCostItem(t(translations.newItem), templates, entity, parentId, companyId);
    setActiveForm('item');
  };
  const onClickDeleteItem = (id) => removeCostItem(id, templates, entity, companyId);

  const onClickAddCategory = (parentId = null, costCodesId = null) => {
    addCostCategory({ name: t(translations.newCategory), parentId, costCodesId }, templates, entity, companyId);
    setActiveForm('category');
  };

  const onClickDeleteCategory = (id) => {
    removeCostCategory(id, templates, entity, companyId);
  };

  const handleSubmitPriceBook = (parentId, priceBookList, importWithLink) => {
    addCostItemsFromPriceBook(templates, priceBookList, parentId, entity, companyId, importWithLink);
    setShowImportFromPriceBook(false);
  };

  const forms = {
    category: (
      <CostCategoryForm
        category={activeCostCategory}
        templates={templates}
        selectedId={selectedId}
        entity={entity}
        updateCostCategory={updateCostCategory}
        costCodesCategories={costCodesCategories}
        costCodesIdToCodeAndNameMap={costCodesIdToCodeAndNameMap}
        costCodesCodeAndNameToIdMap={costCodesCodeAndNameToIdMap}
      />
    ),
    item: (
      <CostItemForm
        entity={entity}
        costItem={activeCostItem}
        templates={templates}
        updateCostItem={updateCostItem}
        groupedOperations={groupedOperations}
        depthUnit={depthUnit}
        volumeUnit={volumeUnit}
      />
    ),
  };
  let form = null;

  if (activeForm) {
    form = forms[activeForm];
  }
  const [costDrawerWidth, setCostDrawerWidth] = useState(300);

  return (
    <>
      <div className={styles.activityModelTabWrapper}>
        <Row height="100%" spacing={0}>
          <Drawer
            width={costDrawerWidth}
            closedWidth={50}
            open
            button
            border
            onResize={(width) => setCostDrawerWidth(width)}
          >
            <Column scroll width="350px" borderRight spacing={0}>
              <CostElementsList
                entity={entity}
                templates={templates}
                selectedId={selectedId}
                elements={elements}
                onClickAddItem={onClickAddItem}
                showDeleteCostItemModalUpdated={showDeleteCostItemModalUpdated}
                showDeleteCostCategoryModalUpdated={showDeleteCostCategoryModalUpdated}
                setActiveForm={setActiveForm}
                onClickAddCategory={onClickAddCategory}
                costItemSelected={costItemSelected}
                costCategorySelected={costCategorySelected}
                reorderElements={reorderElements}
                costCodesIdToCodeMap={costCodesIdToCodeMap}
                setShowImportFromPriceBook={setShowImportFromPriceBook}
                setSelectedImportId={setSelectedImportId}
                setImportWithLink={setImportWithLink}
              />
            </Column>
          </Drawer>
          {(activeCostItem || activeCostCategory) && (
            <Column scroll padding>
              {form}
            </Column>
          )}
        </Row>
      </div>
      {showDeleteCostItemModal && (
        <CostItemDeleteModal
          setModalVisible={showDeleteCostItemModalUpdated}
          costItemId={selectedId}
          onClickDeleteItem={onClickDeleteItem}
        />
      )}
      {showDeleteCostCategoryModal && (
        <CostCategoryDeleteModal
          setModalVisible={showDeleteCostCategoryModalUpdated}
          costCategoryId={selectedId}
          onClickDeleteCategory={onClickDeleteCategory}
        />
      )}
      {showImportFromPriceBook && (
        <ImportFromPriceBookModal
          parentId={selectedImportId}
          showImportFromPriceBookModalUpdated={setShowImportFromPriceBook}
          handleSubmit={handleSubmitPriceBook}
          importWithLink={importWithLink}
        />
      )}
    </>
  );
};

const mapStateToProps = (state, { templates }) => {
  const selectedId = getSelectedId(state);
  const activeTemplate = templates.find((item) => item.active);
  const sectionTypes = selectSectionTypes(state);
  const getGroupedOperations = (sections, operationList) =>
    sections.reduce((acc, { linkedSectionId, sectionName, sectionTypeName }) => {
      const sectionOperation = operationList.filter(({ linkedSectionType }) => linkedSectionType === linkedSectionId);
      if (sectionOperation.length > 0) {
        acc.push({
          linkedSectionId,
          sectionName,
          operations: sectionOperation,
          sectionTypeName,
        });
      }
      return acc;
    }, []);
  const groupedOperations = getGroupedOperations(sectionTypes, activeTemplate?.activityModel || []);
  const costItems = activeTemplate?.costSetup?.costItem || [];
  const activeCostItem = costItems?.find((item) => item.costItemId === selectedId);
  const costCategory = activeTemplate?.costSetup?.costCategory || [];
  const activeCostCategory = costCategory?.find((item) => item.costCategoryId === selectedId);
  const elements = [...costCategory, ...costItems]?.sort((a, b) => a.position - b.position);
  const { showDeleteCostItemModal, showDeleteCostCategoryModal } = selectTemplatesModalVisibility(state);
  return {
    elements,
    showDeleteCostItemModal,
    selectedId,
    activeCostItem,
    activeCostCategory,
    showDeleteCostCategoryModal,
    groupedOperations,
    costCodesCategories: selectCostCodesCategoryOptions(state),
    costCodesIdToCodeAndNameMap: selectCostCodesIdToCodeAndNameMap(state),
    costCodesCodeAndNameToIdMap: selectCostCodesCodeAndNameToIdMap(state),
    costCodesIdToCodeMap: selectCostCodesIdToCodeMap(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getCurrencies,
      addCostItem,
      showDeleteCostItemModalUpdated,
      removeCostItem,
      costItemSelected,
      updateCostItem,
      addCostCategory,
      showDeleteCostCategoryModalUpdated,
      removeCostCategory,
      costCategorySelected,
      updateCostCategory,
      getCostCodesItems,
      reorderElements,
      addCostItemsFromPriceBook,
      getPriceBookCompanyItems,
      clearPriceBook,
    },
    dispatch,
  );

const Container = connect(mapStateToProps, mapDispatchToProps)(CostSetup);

export { Container as CostSetup };
